import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Icon, Input, Button } from "antd";
import { useTranslation } from "react-i18next";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

import styled from "styled-components/macro";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 390px;
  min-height: 390px;
  position: relative;

  .leaflet-custom-area-map {
    width: 100%;
    height: 100%;
    min-height: 390px;
    background: #f0f2f5;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 10px;
`;

const DrawModal = styled(Modal)`
  .ant-modal-body {
    position: relative;
    overflow: hidden;
  }
`;

const TutorialWrapper = styled.div`
  width: 300px;
  position: absolute;
  top: 0;
  right: 0;
  background: white;
  padding: 0 30px;
  border-left: 1px solid #e8e8e8;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-top-right-radius: 5px;
  transform: translateX(0);
  transition: 0.4s ease transform;
  z-index: 1000;

  &.hidden {
    transform: translateX(100%);
  }

  strong {
    width: 100%;
    display: flex;
    border-bottom: 1px solid lightgray;
    margin: 0 0 5px;
    padding: 5px 0;
  }
  ul {
    padding-left: 0;
    list-style-type: none;
  }
`;

const shapeOptions = {
  color: "#3D64FF",
  fillColor: "#3D64FF",
  fillOpacity: 0.25,
  weight: 2
};

const tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const tileLayerAttribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const modes = {
  polygon: "polygon",
  rectangle: "rectangle"
};

const closePolygonRing = points => {
  const ring = points.map(({ lng, lat }) => [lng, lat]);
  return ring.concat([ring[0]]);
};

const rectangleRingFromCorners = (firstCorner, secondCorner) => {
  const north = Math.max(firstCorner.lat, secondCorner.lat);
  const south = Math.min(firstCorner.lat, secondCorner.lat);
  const east = Math.max(firstCorner.lng, secondCorner.lng);
  const west = Math.min(firstCorner.lng, secondCorner.lng);

  if (north === south || east === west) return null;

  return [
    [east, north],
    [east, south],
    [west, south],
    [west, north],
    [east, north]
  ];
};

function DrawAreaModal({ onClose, onSubmit }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawingLayerRef = useRef(null);
  const pointMarkersRef = useRef([]);
  const polygonPointsRef = useRef([]);
  const rectangleFirstCornerRef = useRef(null);
  const drawModeRef = useRef(modes.polygon);
  const { t } = useTranslation(["global", "components"]);
  const [displayName, setDisplayName] = useState("");
  const [coordinates, setCoordinates] = useState([]);
  const [error, setError] = useState("");
  const [drawMode, setDrawMode] = useState(modes.polygon);
  const [tutorialOpened, setTutorialOpened] = useState(false);

  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  const clearMapLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (drawingLayerRef.current) {
      map.removeLayer(drawingLayerRef.current);
      drawingLayerRef.current = null;
    }

    pointMarkersRef.current.forEach(marker => map.removeLayer(marker));
    pointMarkersRef.current = [];
  }, []);

  const updateCoordinates = useCallback(ring => {
    setCoordinates(ring ? [[ring]] : []);
  }, []);

  const clearArea = useCallback(() => {
    clearMapLayers();
    polygonPointsRef.current = [];
    rectangleFirstCornerRef.current = null;
    updateCoordinates(null);
    setError("");
  }, [clearMapLayers, updateCoordinates]);

  const renderPolygon = useCallback(
    points => {
      const map = mapRef.current;
      if (!map) return;

      clearMapLayers();

      pointMarkersRef.current = points.map(point =>
        L.circleMarker(point, {
          radius: 4,
          color: "#3D64FF",
          fillColor: "#3D64FF",
          fillOpacity: 1,
          weight: 1
        }).addTo(map)
      );

      if (points.length === 2) {
        drawingLayerRef.current = L.polyline(points, shapeOptions).addTo(map);
      }

      if (points.length >= 3) {
        drawingLayerRef.current = L.polygon(points, shapeOptions).addTo(map);
        updateCoordinates(closePolygonRing(points));
      } else {
        updateCoordinates(null);
      }
    },
    [clearMapLayers, updateCoordinates]
  );

  const renderRectangle = useCallback(
    ring => {
      const map = mapRef.current;
      if (!map) return;

      clearMapLayers();

      const latLngs = ring.map(([lng, lat]) => [lat, lng]);
      drawingLayerRef.current = L.polygon(latLngs, shapeOptions).addTo(map);
      updateCoordinates(ring);
    },
    [clearMapLayers, updateCoordinates]
  );

  useEffect(() => {
    const initializeMap = () => {
      if (!mapContainerRef.current || mapRef.current) return;

      const map = L.map(mapContainerRef.current, {
        zoomControl: true,
        attributionControl: true
      }).setView([-34.6037, -58.3816], 4);

      L.tileLayer(tileLayerUrl, {
        attribution: tileLayerAttribution,
        maxZoom: 19
      }).addTo(map);

      mapRef.current = map;

      const handleClick = ({ latlng }) => {
        setError("");

        if (drawModeRef.current === modes.rectangle) {
          if (!rectangleFirstCornerRef.current) {
            clearMapLayers();
            rectangleFirstCornerRef.current = latlng;
            pointMarkersRef.current = [
              L.circleMarker(latlng, {
                radius: 4,
                color: "#3D64FF",
                fillColor: "#3D64FF",
                fillOpacity: 1,
                weight: 1
              }).addTo(map)
            ];
            updateCoordinates(null);
            return;
          }

          const ring = rectangleRingFromCorners(
            rectangleFirstCornerRef.current,
            latlng
          );
          rectangleFirstCornerRef.current = null;
          if (ring) renderRectangle(ring);
          return;
        }

        polygonPointsRef.current = polygonPointsRef.current.concat([latlng]);
        renderPolygon(polygonPointsRef.current);
      };

      map.on("click", handleClick);

      [0, 250, 500].forEach(delay => {
        window.setTimeout(() => {
          if (mapRef.current) mapRef.current.invalidateSize(false);
        }, delay);
      });

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude, longitude } }) => {
            if (mapRef.current) {
              mapRef.current.setView([latitude, longitude], 10);
            }
          },
          () => {},
          { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
        );
      }

      mapRef.current.cleanupClick = () => map.off("click", handleClick);
    };

    const timer = window.setTimeout(initializeMap, 300);

    return () => {
      window.clearTimeout(timer);
      if (mapRef.current) {
        if (mapRef.current.cleanupClick) mapRef.current.cleanupClick();
        mapRef.current.remove();
      }
      mapRef.current = null;
      drawingLayerRef.current = null;
      pointMarkersRef.current = [];
    };
  }, [clearMapLayers, renderPolygon, renderRectangle, updateCoordinates]);

  const validate = useCallback(
    (triggerError = true) => {
      if (!displayName) {
        if (triggerError) setError(t("components:DrawAreaModal.errors.noName"));
        return false;
      }
      if (!coordinates || !coordinates[0]) {
        if (triggerError)
          setError(t("components:DrawAreaModal.errors.noAreaSelected"));
        return false;
      }
      return true;
    },
    [coordinates, displayName, t]
  );

  const submit = useCallback(() => {
    if (!validate()) return;
    onSubmit({
      display_name: displayName,
      geojson: {
        type: "MultiPolygon",
        coordinates
      }
    });
    onClose();
  }, [coordinates, displayName, onSubmit, onClose, validate]);

  return (
    <DrawModal
      width={800}
      title={t("components:DrawAreaModal.title")}
      okText={t("global:add")}
      cancelText={t("global:cancel")}
      onCancel={onClose}
      onOk={submit}
      visible={true}
    >
      <Layout>
        <Header>
          <Input
            placeholder={t("components:DrawAreaModal.nameInputPlaceholder")}
            prefix={<Icon type="flag" style={{ color: "rgba(0,0,0,.25)" }} />}
            onChange={e => setDisplayName(e.target.value)}
            value={displayName}
            style={{ width: "50%" }}
          />

          <Button onClick={() => setTutorialOpened(true)} type="primary">
            {t("components:DrawAreaModal.tutorialButton")}
          </Button>
          <Button
            type={drawMode === modes.polygon ? "primary" : "default"}
            onClick={() => {
              setDrawMode(modes.polygon);
              clearArea();
            }}
          >
            Polígono
          </Button>
          <Button
            type={drawMode === modes.rectangle ? "primary" : "default"}
            onClick={() => {
              setDrawMode(modes.rectangle);
              clearArea();
            }}
          >
            Rectángulo
          </Button>
          <Button onClick={clearArea} disabled={!coordinates.length}>
            Limpiar
          </Button>
        </Header>
        <MapContainer>
          <div ref={mapContainerRef} className="leaflet-custom-area-map" />
        </MapContainer>
        {error && (
          <span style={{ color: "red", margin: "15px 0 -10px" }}>{error}</span>
        )}
      </Layout>
      <TutorialWrapper className={!tutorialOpened ? "hidden" : ""}>
        <ul style={{ margin: 0 }}>
          <li>
            <strong>{t("components:DrawAreaModal.step", { step: 1 })}</strong>
            <p>{t("components:DrawAreaModal.stepOne")}</p>
          </li>
          <li>
            <strong>{t("components:DrawAreaModal.step", { step: 2 })}</strong>
            <p>{t("components:DrawAreaModal.stepTwo")}</p>
          </li>
        </ul>
        <Button
          onClick={() => setTutorialOpened(false)}
          icon="close"
          style={{ marginTop: 10 }}
        >
          {t("components:DrawAreaModal.closeTutorial")}
        </Button>
      </TutorialWrapper>
    </DrawModal>
  );
}

export default DrawAreaModal;
